<script setup lang="ts">
/**
 * Страница задачи — макет 13:401: слева контент 920 с ключом, заголовком и
 * вкладками активности, справа мета-панель 280.
 *
 * В адресе ключ (`/issue/DEV-1421`), а не идентификатор: этот адрес пересылают
 * в чате и пишут в коммитах.
 *
 * Заголовок правится прямо здесь, без режима редактирования: карандаш рядом с
 * названием — лишний клик на действии, которое делают чаще всего.
 *
 * Кнопок «Поднять стенд», «Флаг» и «Релиз» в шапке блока GitLab пока нет —
 * DEV-1476.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { AlertTriangle, ArrowLeft } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import IssueAttachments from '@/features/issue/components/IssueAttachments.vue'
import IssueComments from '@/features/issue/components/IssueComments.vue'
import IssueDevInfo from '@/features/issue/components/IssueDevInfo.vue'
import IssueDescription from '@/features/issue/components/IssueDescription.vue'
import IssueHistory from '@/features/issue/components/IssueHistory.vue'
import IssueLinks from '@/features/issue/components/IssueLinks.vue'
import IssueMetaPanel from '@/features/issue/components/IssueMetaPanel.vue'
import IssueSubtasks from '@/features/issue/components/IssueSubtasks.vue'
import { useIssueStore } from '@/features/issue/store'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'

/** Столько же держит сервис и форма создания */
const TITLE_LIMIT = 500

const TABS = ['comments', 'history', 'links'] as const

type Tab = (typeof TABS)[number]

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const store = useIssueStore()

const issueKey = computed(() => String(route.params.key ?? ''))

/**
 * Счётчик на вкладке приезжает с задачей, а потом его правит сама лента:
 * после отправки комментария цифра обязана меняться без перезагрузки.
 */
const commentCount = ref(0)

const tab = computed<Tab>(() => {
  const asked = String(route.query.tab ?? '')

  return (TABS as readonly string[]).includes(asked) ? (asked as Tab) : 'comments'
})

/**
 * «Разработка / Задачи / DEV-1421». Пока проект не приехал, ведём в хаб: ссылка
 * на `/p//board` открыла бы 404.
 */
const breadcrumbs = computed(() => {
  const key = store.project?.key

  return [
    { label: store.project?.name || t('nav.projects'), to: key ? `/p/${key}/board` : '/projects' },
    ...(key ? [{ label: t('views.issues'), to: `/p/${key}/issues` }] : []),
    { label: issueKey.value, mono: true },
  ]
})

const openIssue = async () => {
  await store.open(issueKey.value)

  commentCount.value = store.issue?.commentCount ?? 0

  // Нет такой задачи — 404, а не пустая страница с ключом в крошках.
  if (store.isMissing) {
    await router.replace({ name: 'not-found' })

    return
  }

  const failureRoute = errorRoute(store.error)

  if (failureRoute) await router.replace(failureRoute)
}

const onTitle = async (event: Event) => {
  const field = event.target as HTMLInputElement
  const title = field.value.trim()

  if (title.length > TITLE_LIMIT) {
    toast.error(t('issues.create.errors.title', { limit: TITLE_LIMIT }))
    field.value = store.issue?.title ?? ''

    return
  }

  // Пустое название — не правка, а промах мимо клавиши: возвращаем прежнее.
  if (!title) {
    field.value = store.issue?.title ?? ''

    return
  }

  try {
    await store.setTitle(title)
  } catch (failure) {
    field.value = store.issue?.title ?? ''
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}

const onFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}

/** Подписка на задачу: в макете это первая кнопка шапки */
const toggleWatch = async () => {
  try {
    await store.toggleWatching()
  } catch (failure) {
    onFailed(failure)
  }
}

onMounted(openIssue)

// Ссылка на соседнюю задачу (эпик, подзадача) меняет только параметр маршрута —
// экран при этом не пересоздаётся.
watch(issueKey, openIssue)
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template #lead>
      <button type="button" class="iback" :aria-label="t('issue.back')" @click="router.back()">
        <ArrowLeft :size="16" />
      </button>
    </template>

    <AppButton v-if="store.issue" variant="secondary" size="small" @click="toggleWatch">
      {{ store.isWatching ? t('issue.unwatch') : t('issue.watch') }}
    </AppButton>
  </TrackerToolbar>

  <div class="issue">
    <div class="issue__body">
      <div v-if="store.isLoading && !store.issue" class="issue__skeleton">
        <span class="issue__skeleton-line issue__skeleton-line--key" />
        <span class="issue__skeleton-line issue__skeleton-line--title" />
      </div>

      <TrackerEmptyState
        v-else-if="store.error"
        :icon="AlertTriangle"
        :title="t('issue.error.title')"
        :description="t('issue.error.description')"
        tint="var(--color-danger-tint)"
      >
        <AppButton variant="secondary" @click="openIssue">{{ t('board.error.retry') }}</AppButton>
      </TrackerEmptyState>

      <template v-else-if="store.issue">
        <div class="issue__key">
          <IssueTypeIcon :type="store.issue.type" />
          <span>{{ store.issue.key }}</span>
        </div>

        <input
          class="issue__title"
          :value="store.issue.title"
          :aria-label="t('issue.titleLabel')"
          :maxlength="TITLE_LIMIT"
          @change="onTitle"
          @keydown.enter="($event.target as HTMLInputElement).blur()"
        />

        <IssueDescription @failed="onFailed" />

        <IssueSubtasks @failed="onFailed" />

        <IssueAttachments @failed="onFailed" />

        <IssueDevInfo @failed="onFailed" />

        <section class="iact">
          <nav class="iact__tabs" :aria-label="t('issue.activity.label')">
            <RouterLink
              v-for="name in TABS"
              :key="name"
              class="iact__tab"
              :class="{ 'iact__tab--active': name === tab }"
              :to="{ query: { tab: name } }"
            >
              {{ t(`issue.activity.${name}`) }}
              <span v-if="name === 'comments' && commentCount" class="iact__count">
                {{ commentCount }}
              </span>
            </RouterLink>
          </nav>

          <IssueComments
            v-if="tab === 'comments'"
            @failed="onFailed"
            @counted="commentCount = $event"
          />
          <IssueLinks v-else-if="tab === 'links'" @failed="onFailed" />
          <IssueHistory v-else @failed="onFailed" />
        </section>
      </template>
    </div>

    <IssueMetaPanel @failed="onFailed" />
  </div>
</template>

<style scoped>
.issue {
  display: flex;
  align-items: stretch;
  height: calc(100vh - 48px);
}

.issue__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
  min-width: 0;
  padding: 32px 48px;
  overflow-y: auto;
}

.issue__key {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  line-height: 17px;
}

/* Заголовок сразу поле: рамки нет, пока в него не встали — в макете это текст,
   а не форма. */
.issue__title {
  width: 100%;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 30px;
}

.issue__title:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 4px;
}

.issue__skeleton {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.issue__skeleton-line {
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.issue__skeleton-line--key {
  width: 88px;
  height: 17px;
}

.issue__skeleton-line--title {
  width: 60%;
  height: 30px;
}

.iact {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.iact__tabs {
  display: flex;
  gap: 20px;
  border-bottom: 1px solid var(--color-border);
}

/* Вкладка ростом 28, как в макете: 2 сверху, строка 16, 10 снизу */
.iact__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0 8px;
  border-bottom: 2px solid transparent;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  line-height: 16px;
  text-decoration: none;
}

.iact__tab:hover {
  color: var(--color-text-secondary);
}

.iact__tab--active {
  border-bottom-color: var(--color-primary);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.iact__count {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}

.iact__soon {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

/* Кнопка «назад» — стрелка в квадрате 28: в макете она стоит перед крошками и
   уводит туда, откуда задачу открыли. */
.iback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.iback:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

</style>
